import React, { useCallback, useMemo, useState } from 'react';
import { Col, Container, Row } from 'reactstrap';
import {
  WearableActivitySummaryConnectorApollo,
  WearableSleepSummaryConnectorApollo,
  WearableBodySummaryConnectorApollo,
} from '@front-activerse/common-components';
import type {
  WearableActivitySummaryResult,
  WearableSleepSummaryResult,
  WearableBodySummaryResult,
} from '@front-activerse/common-components';
import type { DateRange, DateRangeKey } from './types';
import { computeRange } from './helpers/ranges';
import ActivitySummarySection from './components/ActivitySummarySection';
import SleepSummarySection from './components/SleepSummarySection';
import BodySummarySection from './components/BodySummarySection';
import NoConnectionEmptyState from './components/NoConnectionEmptyState';
import DateRangeSelector from './components/DateRangeSelector';
import DayNavigator from './components/DayNavigator';
import RefreshButton from './components/RefreshButton';

const isNoConnectionError = (
  error: { graphQLErrors?: ReadonlyArray<{ message?: string }> } | undefined
): boolean => {
  if (!error?.graphQLErrors) return false;
  return error.graphQLErrors.some(
    (e) =>
      typeof e.message === 'string' &&
      e.message.toLowerCase().includes('no active wearable connection')
  );
};

interface ViewProps {
  range: DateRange;
  rangeKey: DateRangeKey;
  setRangeKey: (k: DateRangeKey) => void;
  onShiftDay: (delta: number) => void;
  todayIso: string;
  activity: WearableActivitySummaryResult;
  sleep: WearableSleepSummaryResult;
  body: WearableBodySummaryResult;
}

const WearablesDashboardView: React.FC<ViewProps> = ({
  range,
  rangeKey,
  setRangeKey,
  onShiftDay,
  todayIso,
  activity,
  sleep,
  body,
}) => {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await Promise.allSettled([activity.refetch(), sleep.refetch(), body.refetch()]);
    } finally {
      setIsRefreshing(false);
    }
  }, [activity, sleep, body]);

  const noConnection =
    isNoConnectionError(activity.error) ||
    isNoConnectionError(sleep.error) ||
    isNoConnectionError(body.error);

  if (noConnection) {
    return (
      <Container className="py-xl">
        <Row>
          <Col md={{ size: 8, offset: 2 }}>
            <NoConnectionEmptyState />
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="py-xl">
      <Row>
        <Col md={{ size: 8, offset: 2 }}>
          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
            <div className="flex-shrink-0">
              <DateRangeSelector value={rangeKey} onChange={setRangeKey} />
            </div>
            {range.isSingleDay ? (
              <div className="flex-grow-1 d-flex justify-content-center">
                <DayNavigator
                  date={range.dateFrom}
                  onPrev={() => onShiftDay(-1)}
                  onNext={() => onShiftDay(1)}
                  canGoNext={range.dateFrom < todayIso}
                />
              </div>
            ) : (
              <div className="flex-grow-1" />
            )}
            <div className="flex-shrink-0">
              <RefreshButton onRefresh={handleRefresh} isRefreshing={isRefreshing} />
            </div>
          </div>
          <ActivitySummarySection
            range={range}
            loading={activity.loading}
            error={activity.error}
            activitySummary={activity.activitySummary}
          />
          <SleepSummarySection
            range={range}
            loading={sleep.loading}
            error={sleep.error}
            sleepSummary={sleep.sleepSummary}
          />
          <BodySummarySection
            range={range}
            loading={body.loading}
            error={body.error}
            bodySummary={body.bodySummary}
          />
        </Col>
      </Row>
    </Container>
  );
};

const formatLocalIso = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const shiftIso = (iso: string, deltaDays: number): string => {
  const [y, m, d] = iso.split('-').map(Number);
  const next = new Date(y, m - 1, d);
  next.setDate(next.getDate() + deltaDays);
  return formatLocalIso(next);
};

const WearablesDashboardContainer: React.FC = () => {
  const [rangeKey, setRangeKey] = useState<DateRangeKey>('day');
  const [singleDayDate, setSingleDayDate] = useState<string | null>(null);
  const todayIso = useMemo(() => formatLocalIso(new Date()), []);

  const range = useMemo<DateRange>(() => {
    if (singleDayDate) {
      return {
        key: rangeKey,
        dateFrom: singleDayDate,
        dateTo: singleDayDate,
        isSingleDay: true,
      };
    }
    return computeRange(rangeKey, new Date());
  }, [rangeKey, singleDayDate]);

  const handleSetRangeKey = useCallback((next: DateRangeKey) => {
    setSingleDayDate(null);
    setRangeKey(next);
  }, []);

  const handleShiftDay = useCallback(
    (delta: number) => {
      const base = singleDayDate ?? range.dateFrom;
      setSingleDayDate(shiftIso(base, delta));
    },
    [singleDayDate, range.dateFrom]
  );

  return (
    <WearableActivitySummaryConnectorApollo dateFrom={range.dateFrom} dateTo={range.dateTo}>
      {(activity) => (
        <WearableSleepSummaryConnectorApollo dateFrom={range.dateFrom} dateTo={range.dateTo}>
          {(sleep) => (
            <WearableBodySummaryConnectorApollo>
              {(body) => (
                <WearablesDashboardView
                  range={range}
                  rangeKey={rangeKey}
                  setRangeKey={handleSetRangeKey}
                  onShiftDay={handleShiftDay}
                  todayIso={todayIso}
                  activity={activity}
                  sleep={sleep}
                  body={body}
                />
              )}
            </WearableBodySummaryConnectorApollo>
          )}
        </WearableSleepSummaryConnectorApollo>
      )}
    </WearableActivitySummaryConnectorApollo>
  );
};

export default WearablesDashboardContainer;
export { WearablesDashboardView };
